import { useRef } from 'react';

import produce from 'immer';
import { isEqual, orderBy, pick } from 'lodash-es';
import create from 'zustand';
import shallow from 'zustand/shallow';

import { DefaultFile } from '@components/file-field/types';
import { SelectOption } from '@components/selectmulti';
import { useElementHasFocus } from '@hooks/use-element-has-focus';
import { isSameDay } from '@utils/date';
import { isEditorHtmlEmpty, isSameEditorState } from '@utils/draftjs';
import { uuid } from '@utils/uuid';

import { FormSubmitFn } from './form-provider';
import {
  FormRegisteredFieldData,
  FormStateErrors,
  FormStateInitialization,
  FormStateValues,
  FormStoreState,
} from './types';
import { validateFormValue } from './validate-form-value';

const defaultState: FormStoreState = {
  values: {},
  errors: {},
  registered: {},
  resetCount: 0,
  isSubmitting: false,
  isDisabled: false,
  hasSubmit: false,
  formId: '',
  validationMode: 'onBlur',
};

type SubmitProps = {
  onSubmit: FormSubmitFn<FormStateInitialization>;
  onSubmitSuccess?(data: ReturnType<SubmitProps['onSubmit']>): void;
  action?: string;
};

export const createFormStore = (initialState?: FormStateInitialization) => {
  const formId = uuid();
  const combinedState = {
    ...defaultState,
    ...initialState,
    formId,
  };
  const useStore = create<FormStoreState>(() => combinedState);

  const startSubmit = () => useStore.setState({ isSubmitting: true, hasSubmit: true });
  const finishSubmit = () => useStore.setState({ isSubmitting: false });

  const setDisabled = (isDisabled: boolean) => useStore.setState({ isDisabled });

  const resetStore = () => {
    useStore.setState((state) => ({
      ...combinedState,
      resetCount: state.resetCount + 1,
    }));
  };

  const markAsClean = () => {
    useStore.setState(
      produce<FormStoreState>((state) => {
        Object.keys(state.registered).forEach((key) => {
          if (state.registered[key]) {
            state.registered[key].defaultValue = state.values[key];
          }
        });
      })
    );
  };

  const validateForm = () => {
    let hasErrors = false;

    useStore.setState(
      produce<FormStoreState>((state) => {
        Object.keys(state.registered).forEach((key) => {
          const field = state.registered[key];

          if (!field?.validators) return;

          // Disabled fields shouldn't be validated
          if (field?.isDisabled) {
            delete state.errors[field.id];
            return;
          }

          const fieldErrors = validateFormValue(
            state.values[field.id],
            field.validators,
            field.valueType
          );
          if (fieldErrors.length > 0) {
            state.errors[field.id] = fieldErrors[0];
            hasErrors = true;
          } else {
            delete state.errors[field.id];
          }
        });
      })
    );

    return !hasErrors;
  };

  const submit = async ({ onSubmit, onSubmitSuccess, action = 'submit' }: SubmitProps) => {
    const state = useStore.getState();
    if (state.isSubmitting) return false;

    startSubmit();

    if (action !== 'save' && !validateForm()) {
      finishSubmit();
      return false;
    }

    let result: void | Promise<any> | boolean = false;

    try {
      result = (await onSubmit(state.values, action)) as void | Promise<any>;
      if (result) {
        onSubmitSuccess?.(result);
      }
    } catch (error) {
      result = false;
    } finally {
      finishSubmit();
    }
    return result;
  };

  const validateField = (id: string) => {
    const state = useStore.getState();
    const field = state.registered[id];

    if (!field) return false;

    // Disabled fields and forms shouldn't be validated
    if (field.isDisabled || state.isDisabled) return true;

    if (state.validationMode === 'onSubmit') return true;

    const fieldErrors = validateFormValue(state.values[id], field.validators, field.valueType);

    const errors = { ...state.errors };

    if (fieldErrors.length > 0) {
      errors[id] = fieldErrors[0];
    } else {
      delete errors[id];
    }

    setErrors(errors);

    return fieldErrors.length === 0;
  };

  const setErrors = (errors: FormStateErrors) => {
    useStore.setState(
      produce<FormStoreState>((state) => {
        state.errors = errors;
      })
    );
  };

  const updateErrors = (errors: FormStateErrors) => {
    useStore.setState(
      produce<FormStoreState>((state) => {
        state.errors = { ...state.errors, ...errors };
      })
    );
  };

  const setValues = (values: FormStateValues) => {
    useStore.setState(
      produce<FormStoreState>((state) => {
        state.values = values;
      })
    );
  };

  const updateValues = (values: FormStateValues) => {
    useStore.setState(
      produce<FormStoreState>((state) => {
        state.values = { ...state.values, ...values };
      })
    );
  };

  const setValue = (id: string, newValue: unknown) => {
    useStore.setState(
      produce<FormStoreState>((state) => {
        state.values[id] = newValue;
        delete state.errors[id];
      })
    );

    const state = useStore.getState();
    if (state.validationMode === 'onChange') {
      validateField(id);
    }
  };

  const unregister = (id: string) => {
    useStore.setState(
      produce<FormStoreState>((state) => {
        delete state.registered[id];
        delete state.errors[id];
        delete state.values[id];
      })
    );
  };

  const register = (data: FormRegisteredFieldData) => {
    useStore.setState(
      produce<FormStoreState>((state) => {
        state.registered[data.id] = data;
        state.values[data.id] = state.values[data.id] ?? data.defaultValue;
      })
    );
  };

  const useFieldValue = <T>(id: string) => {
    return useStore((store) => {
      return store.values[id] as T | undefined;
    });
  };

  const useFormHasErrors = () => useStore((state) => Object.keys(state.errors).length > 0);
  const useIsSubmitting = () => useStore((state) => state.isSubmitting);
  const useFieldError = (id: string) => useStore((store) => store.errors[id]);
  const useFormResetCount = () => useStore((store) => store.resetCount);
  const useFormErrors = () => useStore((store) => store.errors);
  const useFormValues = () => useStore((store) => store.values);
  const useIsFormDisabled = () => useStore((store) => store.isDisabled);
  const useHasFocus = () => {
    const { formId } = useStore.getState();
    const resetCount = useFormResetCount();
    const hasFocus = useElementHasFocus(formId, [resetCount]);

    return hasFocus;
  };

  const useIsSubmitDisabled = (action?: string, shouldDisableWhenNotDirty?: boolean) => {
    const hasErrors = useFormHasErrors();
    const isDisabled = useIsFormDisabled();
    const isSubmitting = useIsSubmitting();
    const isDirty = useIsDirty();
    const isNotDirtyAndShouldDisable = shouldDisableWhenNotDirty && !isDirty;
    const shouldCheckErrors = action !== 'save';
    return (
      isNotDirtyAndShouldDisable || isDisabled || (shouldCheckErrors && hasErrors) || isSubmitting
    );
  };

  const useWatchValues = (keys: string[]) => useStore((store) => pick(store.values, keys), shallow);
  const useHasSubmit = () => useStore((store) => store.hasSubmit);
  const useValidationMode = () => useStore((store) => store.validationMode);

  const getTextEditorValue = (value: string | object) => {
    if (!value) return '';
    const html = typeof value === 'string' ? value : value['value' as keyof typeof value];
    return html ?? '';
  };

  const checkIsDefaultFile = (value: unknown): value is DefaultFile => {
    return typeof value === 'object' && value !== null && !(value instanceof File);
  };

  const useIsDirty = () => {
    const registered = useStore((state) => state.registered);
    const values = useFormValues();
    const keys = Object.keys(registered);
    return keys.some((key) => {
      const registeredValue = registered[key].defaultValue;
      const stateValue = values[key];
      const valueType = registered[key].valueType;
      if (valueType === 'text-editor') {
        const registeredHtml = getTextEditorValue(registeredValue as string);
        const stateHtml = getTextEditorValue(stateValue as string);
        if (isEditorHtmlEmpty(registeredHtml) && isEditorHtmlEmpty(stateHtml)) return false;
        if (!isSameEditorState(registeredHtml, stateHtml)) return true;
        const registeredVariables =
          registeredValue && typeof registeredValue === 'object'
            ? (registeredValue as Record<string, unknown>).variables
            : undefined;
        const stateVars =
          stateValue && typeof stateValue === 'object'
            ? (stateValue as Record<string, unknown>).variables
            : undefined;
        return !isEqual(registeredVariables, stateVars);
      } else if (valueType === 'date') {
        if (!registeredValue && !stateValue) return false;
        const registeredDate = new Date(registeredValue as string);
        const stateDate = new Date(stateValue as string);
        return !isSameDay(registeredDate, stateDate);
      } else if (valueType === 'select-multi') {
        return !isEqual(
          orderBy(registeredValue as SelectOption[], ['value']),
          orderBy(stateValue as SelectOption[], ['value'])
        );
      } else if (valueType === 'file') {
        if (!registeredValue && !stateValue) return false;
        if (checkIsDefaultFile(registeredValue) && checkIsDefaultFile(stateValue)) {
          return new URL(registeredValue.src).pathname !== new URL(stateValue.src).pathname;
        } else {
          return !isEqual(registeredValue, stateValue);
        }
      } else {
        if (!registeredValue && !stateValue) return false;
        return !isEqual(registeredValue, stateValue);
      }
    });
  };

  return {
    actions: {
      finishSubmit,
      markAsClean,
      register,
      resetStore,
      setDisabled,
      setErrors,
      setValue,
      setValues,
      startSubmit,
      submit,
      unregister,
      updateErrors,
      updateValues,
      validateField,
      validateForm,
    },
    getState: useStore.getState,
    useFieldError,
    useFieldValue,
    useFormErrors,
    useFormHasErrors,
    useFormResetCount,
    useFormValues,
    useHasSubmit,
    useIsFormDisabled,
    useIsSubmitDisabled,
    useIsSubmitting,
    useWatchValues,
    useIsDirty,
    useHasFocus,
    useValidationMode,
  };
};

export type FormStoreInstance = ReturnType<typeof createFormStore>;

export const useCreateFormStore = (initialState?: FormStateInitialization) => {
  return useRef(createFormStore(initialState)).current;
};
